import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FaBell, FaSync } from 'react-icons/fa';

import useTokenStore from '../stores/tokens';
import { compatApi } from '../utils/Intercepter';
import dayjs from 'dayjs';
import VatCalculator from '../components/VatCalculator';
import { PURPLE, MINT, hexToRgba } from '../utils/designUtils';
import { useIsMobile } from '../utils/responsive';

// ─── 전자결재 현황 스탯 정의 ──────────────────────────────────────────────────
const EAPP_STAT_DEFS = [
    {
        key: 'inboxPending', label: '결재 대기', color: '#5d8fe0', iconBg: 'rgba(93,143,224,.12)',
        route: '/approval/inbox',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
    },
    {
        key: 'draftPending', label: '내 진행중', color: null, iconBg: null,
        route: '/approval/draft-box',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 4v5h-5"/></svg>,
    },
    {
        key: 'draftApproved', label: '승인 완료', color: null, iconBg: null,
        route: '/approval/draft-box',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5L9 17.5L20 6.5"/></svg>,
    },
    {
        key: 'draftRejected', label: '반려', color: '#c44569', iconBg: 'rgba(196,69,105,.12)',
        route: '/approval/draft-box',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>,
    },
];

const REPORT_STAT_DEFS = [
    {
        key: 'receivedTotal', label: '받은 보고', color: PURPLE, iconBg: hexToRgba(PURPLE, .12),
        route: '/report', statusFilter: '전체',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 9h10M7 13h10M7 17h6"/></svg>,
    },
    {
        key: 'receivedUnread', label: '미열람', color: '#5d8fe0', iconBg: 'rgba(93,143,224,.12)',
        route: '/report', statusFilter: '미열람',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16v12H4z"/><path d="M4 7l8 7 8-7"/></svg>,
    },
    {
        key: 'receivedCommented', label: '코멘트 완료', color: MINT, iconBg: hexToRgba(MINT, .12),
        route: '/report', statusFilter: '코멘트 완료',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    },
    {
        key: 'receivedRead', label: '열람 완료', color: '#8b8a99', iconBg: 'rgba(139,138,153,.12)',
        route: '/report', statusFilter: '열람',
        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>,
    },
];

// ─── Donut 진행률 SVG ────────────────────────────────────────────────────────
function Donut({ value, color, trackColor = "#eef0f4", size = 60, strokeW = 6 }) {
    const r = (size - strokeW) / 2;
    const c = 2 * Math.PI * r;
    const dash = (value / 100) * c;
    return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", flexShrink: 0 }}>
            <circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={strokeW} fill="none" />
            <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={strokeW} fill="none"
                strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: "stroke-dasharray 600ms cubic-bezier(.2,.8,.2,1)" }} />
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
                fontSize={size * 0.24} fontWeight="700" fill="#2a2a3a">
                {value}%
            </text>
        </svg>
    );
}

// ─── 카드 공통 스타일 ─────────────────────────────────────────────────────────
const card = {
    background: "white", borderRadius: 16, padding: "20px 24px",
    boxShadow: "0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)",
    border: "1px solid #f0eff5",
};

// ─── 알림 카테고리 메타 ───────────────────────────────────────────────────────
const NOTIF_CAT_META = {
    APPROVAL: {
        label: '결재', c: '#c44569', bg: 'rgba(196,69,105,.10)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M9 14l2 2 4-4" /></svg>
    },
    NOTICE: {
        label: '공지', c: '#7c6bd9', bg: 'rgba(124,107,217,.10)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l18-7v16L3 13z" /><path d="M11 19v2" /></svg>
    },
    SCHEDULE: {
        label: '일정', c: '#5bc2b2', bg: 'rgba(91,194,178,.12)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
    },
    ORG: {
        label: '조직', c: '#5d8fe0', bg: 'rgba(93,143,224,.12)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><circle cx="17" cy="9" r="2.5" /><path d="M15 20a4.5 4.5 0 0 1 6 0" /></svg>
    },
    REPORT: {
        label: '보고', c: '#3a8b8e', bg: 'rgba(58,139,142,.12)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M8 13h8M8 17h5" /></svg>
    },
    PERSONAL: {
        label: '개인', c: '#9b7cb9', bg: 'rgba(155,124,185,.12)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
    },
    ALARM: {
        label: '알림', c: '#d97757', bg: 'rgba(217,119,87,.10)',
        icon: (s) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>
    },
};

const TYPE_TO_CODE = {
    '알림': 'ALARM', '공지': 'NOTICE', '일정': 'SCHEDULE',
    '조직': 'ORG', '결재': 'APPROVAL', '개인': 'PERSONAL',
    '보고': 'REPORT', '프로젝트': 'ORG', '회의': 'NOTICE',
    '선택 안 함': 'ALARM',
};

// ─── 알림 상세 모달 서브 컴포넌트 ────────────────────────────────────────────
function ndIconBtn() {
    return {
        width: 34, height: 34, borderRadius: 9,
        background: "transparent", border: "none", cursor: "pointer",
        color: "#8b8a99", display: "inline-flex", alignItems: "center", justifyContent: "center",
        transition: "all 120ms",
    };
}

function NDSection({ title, accent, children }) {
    return (
        <section style={{
            background: "white", borderRadius: 14,
            border: "1px solid #ececf2",
            boxShadow: "0 1px 2px rgba(60,50,120,.03)",
            overflow: "hidden",
        }}>
            <div style={{
                padding: "11px 18px",
                background: "linear-gradient(180deg, #faf9fd 0%, white 100%)",
                borderBottom: "1px solid #f0eff5",
                display: "flex", alignItems: "center", gap: 8,
            }}>
                <span style={{ width: 4, height: 14, borderRadius: 2, background: accent, flexShrink: 0 }} />
                <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1f1d2e", letterSpacing: "-0.005em" }}>{title}</h3>
            </div>
            <div style={{ padding: "16px 18px" }}>{children}</div>
        </section>
    );
}

function NDMetaGrid({ children }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px 24px" }}>
            {children}
        </div>
    );
}

function NDMeta({ label, children }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
            <span style={{ fontSize: 11, color: "#8b8a99", fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase" }}>{label}</span>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>{children}</div>
        </div>
    );
}

function NDPerson({ name }) {
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: "#1f1d2e" }}>
            <span style={{
                width: 24, height: 24, borderRadius: "50%",
                background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                color: "white", fontSize: 10.5, fontWeight: 700,
                display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>{name.slice(-2)}</span>
            <span style={{ fontWeight: 600 }}>{name}</span>
        </span>
    );
}

export default function Home() {
    const navigate = useNavigate();
    const { user } = useTokenStore();
    const isMobile = useIsMobile(768);
    const [ongoingProjects, setOngoingProjects] = useState([]);
    const [upcomingProjects, setUpcomingProjects] = useState([]);
    const [projectTab, setProjectTab] = useState('ongoing'); // 'ongoing' | 'upcoming'
    const [activities, setActivities] = useState([]);
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [showPushDropdown, setShowPushDropdown] = useState(false);
    const [eappStats, setEappStats] = useState({ inboxPending: 0, draftPending: 0, draftApproved: 0, draftRejected: 0 });
    const [reportStats, setReportStats] = useState({ receivedTotal: 0, receivedUnread: 0, receivedCommented: 0, receivedRead: 0 });

    const getCategoryLabel = (code) => {
        switch (code) {
            case 'ALARM': return '알림';
            case 'NOTICE': return '공지';
            case 'SCHEDULE': return '일정';
            case 'ORG': return '조직';
            case 'APPROVAL': return '결재';
            case 'PERSONAL': return '개인';
            case 'REPORT': return '보고';
            default: return '선택 안 함';
        }
    };

    const getRelativeTime = (dateStr) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return '방금';
        if (mins < 60) return `${mins}분 전`;
        const hrs = Math.floor(diff / 3600000);
        if (hrs < 24) return `${hrs}시간 전`;
        if (Math.floor(diff / 86400000) === 1) return '어제';
        return new Date(dateStr).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }).replace('. ', '.').replace('.', '').trim();
    };

    useEffect(() => {
        if (user?.currentOrganizationCode) {
            loadProjects();
            loadActivities();
            loadEappStats();
            loadReportStats();
        }
    }, [user?.currentOrganizationCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadProjects = async () => {
        try {
            const response = user.currentRole === 'MASTER'
                ? await compatApi.post('/project/readProjectList', user.currentOrganizationCode)
                : await compatApi.post('/project/readMyProjectList', user.currentOrganizationCode);

            if (response.ok) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const projects = response.data.map(project => {
                    const contractualStartDate = project.contractualStartDate ? new Date(project.contractualStartDate) : null;
                    const contractualEndDate = project.contractualEndDate ? new Date(project.contractualEndDate) : null;
                    const actualStartDate = project.startDate ? new Date(project.startDate) : null;
                    const actualEndDate = project.endDate ? new Date(project.endDate) : null;

                    const startDate = project.startDate ? actualStartDate : contractualStartDate;
                    const endDate = project.endDate ? actualEndDate : contractualEndDate;

                    let status = '예정';
                    if (today < startDate) status = '예정';
                    else if (startDate <= today && today <= endDate) status = '진행중';
                    else status = '종료';

                    const contractualMetrics = calculateProjectMetrics(contractualStartDate, contractualEndDate);
                    const actualMetrics = calculateProjectMetrics(actualStartDate, actualEndDate);

                    return {
                        ...project, status,
                        contractualProgress: status === '진행중' ? contractualMetrics.progress : 0,
                        actualProgress: status === '진행중' ? actualMetrics.progress : 0,
                        totalDays: contractualMetrics.totalDays,
                        elapsedDays: status === '진행중' ? contractualMetrics.elapsedDays : 0,
                        totalMonths: contractualMetrics.totalMonths,
                    };
                });
                setOngoingProjects(projects.filter(p => p.status === '진행중'));
                setUpcomingProjects(projects.filter(p => p.status === '예정'));
            }
        } catch (error) {
            console.error('프로젝트 로드 오류:', error);
        }
    };

    const calculateProjectMetrics = (start, end) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isNull = start === null || end === null;
        const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
        const elapsedDays = Math.max(0, Math.ceil((today - start) / (1000 * 60 * 60 * 24)) + 1);
        const progress = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
        const totalMonths = Math.ceil(dayjs(end).diff(dayjs(start), 'months', true) * 10) / 10;
        return {
            totalDays: isNull ? 0 : totalDays,
            elapsedDays: isNull ? 0 : elapsedDays,
            progress: isNull ? 0 : progress,
            totalMonths: isNull ? 0 : totalMonths,
        };
    };

    const handleMarkRead = async (id) => {
        try {
            await compatApi.put(`/push/device/history/${id}`, { userEmail: user.userEmail });
            setActivities(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
        } catch (e) {
            console.error('읽음 처리 실패', e);
        }
    };

    const handleActivityClick = (activity) => {
        setSelectedActivity(activity);
        if (!activity.read) {
            handleMarkRead(activity.id);
        }
    };

    const loadActivities = async () => {
        try {
            const response = await compatApi.get('/push/device/history/web');
            if (response.ok) {
                const mapped = response.data.map(item => {
                    let body = '';
                    try { body = JSON.parse(item.extData).body || ''; } catch { body = item.extData || ''; }
                    let typeLabel = item.categoryCode ? getCategoryLabel(item.categoryCode) : '알림';
                    if (!item.categoryCode && item.type) {
                        if (item.type === 'PROJECT') typeLabel = '프로젝트';
                        else if (item.type === 'MEETING') typeLabel = '회의';
                        else if (item.type === 'NOTICE') typeLabel = '공지';
                    }
                    const doc = new DOMParser().parseFromString(body, 'text/html');
                    const textBody = doc.body.textContent || "";

                    return {
                        id: item.id, type: typeLabel, title: item.title || '',
                        message: body, textMessage: textBody, time: getRelativeTime(item.regDt),
                        regDt: item.regDt, read: item.read,
                    };
                });
                setActivities(mapped);
            }
        } catch (error) {
            console.error('활동 로드 오류:', error);
        }
    };

    const loadEappStats = async () => {
        try {
            const [inboxRes, draftRes] = await Promise.allSettled([
                compatApi.get('/approval/inbox'),
                compatApi.get('/approval/draft-box', { params: { organizationCode: user?.currentOrganizationCode } }),
            ]);
            const thisYear = String(new Date().getFullYear());
            const inbox = (inboxRes.status === 'fulfilled' ? (inboxRes.value.data || []) : [])
                .filter(i => i.submittedAt?.startsWith(thisYear));
            const draft = (draftRes.status === 'fulfilled' ? (draftRes.value.data || []) : [])
                .filter(i => i.submittedAt?.startsWith(thisYear));
            setEappStats({
                inboxPending: inbox.filter(i => i.status === 'PENDING').length,
                draftPending: draft.filter(i => i.status === 'PENDING').length,
                draftApproved: draft.filter(i => i.status === 'APPROVED').length,
                draftRejected: draft.filter(i => i.status === 'REJECTED').length,
            });
        } catch {
            // 통계 로드 실패 시 기본값 유지
        }
    };

    const loadReportStats = async () => {
        try {
            const [receivedRes] = await Promise.allSettled([
                compatApi.get('/report/received', { params: { orgCode: user?.currentOrganizationCode } }),
            ]);
            const thisYear = String(new Date().getFullYear());
            const received = (receivedRes.status === 'fulfilled' ? (receivedRes.value.data || []) : [])
                .filter(r => r.writtenAt?.startsWith(thisYear));

            const myEmail = user?.userEmail;
            const total = received.length;
            const unread = received.filter(r => r.receivers?.find(rc => rc.email === myEmail)?.readYn === 'N').length;
            const commented = received.filter(r => r.receivers?.find(rc => rc.email === myEmail)?.commentText).length;
            const read = received.filter(r => {
                const me = r.receivers?.find(rc => rc.email === myEmail);
                return me?.readYn === 'Y' && !me?.commentText;
            }).length;

            setReportStats({
                receivedTotal: total,
                receivedUnread: unread,
                receivedCommented: commented,
                receivedRead: read,
            });
        } catch (e) {
            console.error('보고서 통계 로드 실패', e);
        }
    };

    // ─── 비로그인 ─────────────────────────────────────────────────────────────
    if (!user) {
        return (
            <div style={{ textAlign: "center", padding: "80px 20px" }}>
                <h2>로그인이 필요합니다.</h2>
                <Link to="/" style={{ padding: '12px 28px', borderRadius: 10, background: PURPLE, color: 'white', textDecoration: 'none', fontWeight: 600, fontSize: 14 }}>로그인하러 가기</Link>
            </div>
        );
    }

    // ─── 어드민 대시보드 ──────────────────────────────────────────────────────
    if (user.currentRole === 'ADMIN' || user.isAdmin) {
        return (
            <div style={{ minHeight: "calc(100vh - 61px)", background: "#f5f4fa", padding: "32px 24px" }}>
                <div style={{ maxWidth: 560, margin: "0 auto" }}>
                    <div style={{ marginBottom: 24 }}>
                        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#1f1d2e" }}>
                            안녕하세요, <span style={{
                                background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
                            }}>[ADMIN]</span>님!
                        </h1>
                    </div>
                    <div style={{ ...card }}>
                        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600, color: "#1f1d2e" }}>관리자 메뉴</h3>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <Link to="/admin/pendingAppr" style={adminLinkStyle(PURPLE)}>
                                <span>조직 관리</span><span>→</span>
                            </Link>
                            <Link to="/admin/readUserList" style={adminLinkStyle(PURPLE)}>
                                <span>회원 관리</span><span>→</span>
                            </Link>
                            {/* 푸시 관리 드롭다운 */}
                            <div style={{ position: "relative" }}
                                onMouseEnter={() => setShowPushDropdown(true)}
                                onMouseLeave={() => setShowPushDropdown(false)}>
                                <div style={{ ...adminLinkStyle(PURPLE), cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>푸시 관리</span><span>{showPushDropdown ? '▾' : '→'}</span>
                                </div>
                                {showPushDropdown && (
                                    <div style={{
                                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
                                        background: "white", borderRadius: 10, border: "1px solid #f0eff5",
                                        boxShadow: "0 8px 24px -8px rgba(0,0,0,.2)", padding: "6px 0",
                                    }}>
                                        {[
                                            ["/admin/push/list", "푸시 목록"],
                                            ["/admin/push/create", "푸시 생성"],
                                            ["/admin/push/template/list", "템플릿 목록"],
                                            ["/admin/push/template/create", "템플릿 생성"],
                                            ["/admin/settings", "어드민 설정"],
                                        ].map(([to, label]) => (
                                            <Link key={to} to={to} style={{
                                                display: "block", padding: "9px 16px",
                                                fontSize: 13.5, color: "#3a3849", textDecoration: "none",
                                            }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f4fa"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                                                {label}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ─── 소속 조직 없음(퇴사·탈퇴 등으로 유효한 조직이 하나도 없는 상태) ──────────
    // "승인 대기"와는 다른 상황이라 구분한다 — 조직 자체가 없으니 안내 문구도, 유도할 행동도 다르다.
    if (!user.currentOrganizationCode) {
        return (
            <div style={{
                minHeight: "calc(100vh - 61px)", background: "#f5f4fa",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
            }}>
                <div style={{ ...card, maxWidth: 480, width: "100%", textAlign: "center", padding: "48px 40px" }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🏢</div>
                    <h2 style={{ margin: "0 0 12px", fontWeight: 700, color: "#1f1d2e" }}>소속된 조직이 없습니다</h2>
                    <p style={{ margin: "0 0 24px", fontSize: 14, color: "#5d5b6e", lineHeight: 1.7 }}>
                        새 조직을 만들거나, 기존 조직에 가입해 주세요.
                    </p>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                        <Link to="/organization/orgAdd" style={{
                            height: 42, padding: "0 22px", borderRadius: 10,
                            background: `linear-gradient(135deg, ${PURPLE}, ${MINT})`,
                            color: "white", fontSize: 14, fontWeight: 600,
                            display: "inline-flex", alignItems: "center", textDecoration: "none",
                            boxShadow: `0 6px 14px -6px ${hexToRgba(PURPLE, .55)}`,
                        }}>조직 생성</Link>
                        <Link to="/organization/join" style={{
                            height: 42, padding: "0 22px", borderRadius: 10,
                            background: "white", border: `1.5px solid ${hexToRgba(PURPLE, .3)}`,
                            color: PURPLE, fontSize: 14, fontWeight: 600,
                            display: "inline-flex", alignItems: "center", textDecoration: "none",
                        }}>조직 가입</Link>
                    </div>
                </div>
            </div>
        );
    }

    // ─── 승인 대기 ────────────────────────────────────────────────────────────
    if (!user.currentOrganizationApproved) {
        return (
            <div style={{
                minHeight: "calc(100vh - 61px)", background: "#f5f4fa",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
            }}>
                <div style={{ ...card, maxWidth: 480, width: "100%", textAlign: "center", padding: "48px 40px" }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
                    <h2 style={{ margin: "0 0 12px", fontWeight: 700, color: "#d97757" }}>회원가입 승인 대기 중</h2>
                    <p style={{ margin: 0, fontSize: 14, color: "#5d5b6e", lineHeight: 1.7 }}>
                        현재 <strong style={{ color: PURPLE }}>{user.currentOrganizationName}</strong> 조직에 대한<br />
                        회원가입 승인 절차가 진행 중입니다.<br />
                        문의사항은 조직 관리자에게 문의해 주세요.
                    </p>
                </div>
            </div>
        );
    }

    // ─── 일반 사용자 대시보드 ─────────────────────────────────────────────────
    const unreadActivities = activities
        .filter(a => !a.read)
        .sort((a, b) => {
            const diff = new Date(a.regDt) - new Date(b.regDt);
            return sortOrder === 'desc' ? -diff : diff;
        });

    return (
        <>
            {/* 활동 상세 모달 */}
            {selectedActivity && (() => {
                const cm = NOTIF_CAT_META[TYPE_TO_CODE[selectedActivity.type]] || NOTIF_CAT_META.ALARM;
                return (
                    <div onClick={() => setSelectedActivity(null)} style={{
                        position: 'fixed', inset: 0, zIndex: 1050,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
                        background: 'rgba(28,26,46,.45)',
                        backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
                    }}>
                        <style>{`
                            @keyframes nd-rise-in { from {opacity:0; transform:translateY(8px) scale(.99)} to {opacity:1; transform:none} }
                        `}</style>
                        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{
                            position: 'relative', zIndex: 1,
                            width: 'min(620px, 100%)', maxHeight: 'calc(100vh - 48px)',
                            background: 'white', borderRadius: 18,
                            boxShadow: '0 30px 60px -20px rgba(20,15,40,.4), 0 8px 22px -10px rgba(20,15,40,.25)',
                            display: 'flex', flexDirection: 'column', overflow: 'hidden',
                            animation: 'nd-rise-in 220ms cubic-bezier(.2,.8,.2,1)',
                        }}>
                            {/* 헤더 */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '18px 22px', borderBottom: '1px solid #ececf2',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: 11,
                                        background: `linear-gradient(135deg, ${hexToRgba(cm.c, .18)}, ${hexToRgba(cm.c, .32)})`,
                                        color: cm.c,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>{cm.icon(18)}</div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                                fontSize: 10.5, fontWeight: 700, color: cm.c,
                                                background: cm.bg, padding: '2px 8px', borderRadius: 5,
                                            }}>
                                                <span style={{ width: 5, height: 5, borderRadius: '50%', background: cm.c }} />
                                                {cm.label}
                                            </span>
                                            <span style={{ fontSize: 11.5, color: '#8b8a99', fontWeight: 600, letterSpacing: '.04em' }}>
                                                ALERT DETAIL
                                            </span>
                                        </div>
                                        <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em' }}>
                                            알림 상세 보기
                                        </h2>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedActivity(null)} title="닫기" style={ndIconBtn()}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f3f9'; e.currentTarget.style.color = '#1f1d2e'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8b8a99'; }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* 바디 */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px 8px', background: '#fafaff' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <NDSection title="기본 정보" accent={PURPLE}>
                                        <NDMetaGrid>
                                            <NDMeta label="카테고리">
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    fontSize: 12, fontWeight: 600, color: cm.c,
                                                    background: cm.bg, padding: '4px 10px', borderRadius: 6,
                                                }}>
                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: cm.c }} />
                                                    {cm.label}
                                                </span>
                                            </NDMeta>
                                            <NDMeta label="수신자">
                                                <NDPerson name={user.name || user.userName || '사용자'} />
                                            </NDMeta>
                                            <NDMeta label="수신 일시">
                                                <span style={{ fontSize: 13, color: '#1f1d2e', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                                    {dayjs(selectedActivity.regDt).format('YYYY-MM-DD HH:mm')}
                                                </span>
                                                <span style={{ fontSize: 11.5, color: '#8b8a99' }}>· {selectedActivity.time}</span>
                                            </NDMeta>
                                        </NDMetaGrid>
                                    </NDSection>

                                    <NDSection title="메시지 내용" accent={MINT}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1f1d2e', letterSpacing: '-0.01em', marginBottom: 8, lineHeight: 1.4 }}>
                                            {selectedActivity.title || `${cm.label} 알림`}
                                        </div>
                                        <div style={{ fontSize: 13.5, color: '#3a3849', lineHeight: 1.6 }}
                                            dangerouslySetInnerHTML={{ __html: selectedActivity.message }} />
                                    </NDSection>
                                </div>
                            </div>

                            {/* 푸터 */}
                            <div style={{
                                display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8,
                                padding: '14px 22px', borderTop: '1px solid #ececf2', background: 'white',
                            }}>
                                <button onClick={() => setSelectedActivity(null)} style={{
                                    height: 38, padding: '0 16px', borderRadius: 9,
                                    background: 'white', border: '1.5px solid #ececf2',
                                    color: '#5d5b6e', fontSize: 13, fontWeight: 600,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                }}>닫기</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <div style={{ minHeight: "calc(100vh - 61px)", background: "#f5f4fa" }}>
                <main style={{ maxWidth: 1600, margin: "0 auto", padding: "32px clamp(20px, 3vw, 40px) 48px" }}>

                    {/* 환영 히어로 배너 */}
                    <div style={{
                        background: "white", borderRadius: 16,
                        border: "1px solid #f0eff5",
                        boxShadow: "0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)",
                        overflow: "hidden", marginBottom: 24,
                    }}>
                        {/* 그라디언트 배너 */}
                        <div style={{
                            position: "relative",
                            background: `linear-gradient(135deg, ${hexToRgba(PURPLE, .92)} 0%, ${hexToRgba(MINT, .85)} 100%)`,
                            padding: "28px 32px 96px",
                            color: "white",
                        }}>
                            {/* 도트 패턴 장식 */}
                            <svg width="180" height="180" viewBox="0 0 180 180" style={{ position: "absolute", right: -24, top: -34, opacity: .32, pointerEvents: "none" }}>
                                <defs>
                                    <pattern id="dotpat-hero" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                                        <circle cx="2" cy="2" r="1.4" fill="white"/>
                                    </pattern>
                                </defs>
                                <rect width="180" height="180" fill="url(#dotpat-hero)"/>
                            </svg>

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, position: "relative", flexWrap: "wrap" }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .9 }}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".14em", textTransform: "uppercase", opacity: .9 }}>
                                            Welcome back
                                        </span>
                                    </div>
                                    <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: "-0.015em", color: "white" }}>
                                        안녕하세요, {user.name || user.userName} 님!
                                    </h1>
                                    <p style={{ margin: "8px 0 0", fontSize: 14, color: "rgba(255,255,255,.88)" }}>
                                        현재 진행중인 프로젝트와 전자결재 현황을 한눈에 확인하세요.
                                    </p>
                                </div>
                                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                    <Link to="/calendar" style={{
                                        height: 38, padding: "0 16px", borderRadius: 10,
                                        background: "rgba(255,255,255,.18)",
                                        border: "1px solid rgba(255,255,255,.32)",
                                        color: "white", fontSize: 13, fontWeight: 500,
                                        display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                                        backdropFilter: "blur(4px)", textDecoration: "none",
                                        cursor: "pointer",
                                    }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>
                                        {dayjs().format('YYYY년 M월')}
                                    </Link>
                                    <Link to="/project/create" style={{
                                        height: 38, padding: "0 16px", borderRadius: 10,
                                        background: "white", border: "none",
                                        color: PURPLE, fontSize: 13, fontWeight: 600,
                                        boxShadow: "0 6px 14px -6px rgba(20,15,40,.25)",
                                        textDecoration: "none", display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
                                    }}>
                                        + 새 프로젝트
                                    </Link>
                                    <Link to="/gantt-chart" style={{
                                        height: 38, padding: "0 16px", borderRadius: 10,
                                        background: "white", border: "none",
                                        color: PURPLE, fontSize: 13, fontWeight: 600,
                                        boxShadow: "0 6px 14px -6px rgba(20,15,40,.25)",
                                        textDecoration: "none", display: "inline-flex",
                                        alignItems: "center", whiteSpace: "nowrap", gap: 6,
                                    }}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "translateY(-1.5px)" }}>
                                            <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                                            <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                                        </svg>
                                        프로젝트 차트 보기
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* 현황 플로팅 카드 영역 */}
                        <div style={{
                            margin: isMobile ? "0 12px 16px" : "-66px 22px 22px",
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                            gap: isMobile ? 12 : 20,
                            position: "relative", zIndex: 10,
                        }}>
                            {/* 전자결재 현황 카드 */}
                            <div style={{
                                background: "white", borderRadius: 14,
                                border: "1px solid #f0eff5",
                                boxShadow: "0 8px 24px -10px rgba(60,50,120,.18)",
                                overflow: "hidden",
                            }}>
                                <div style={{
                                    padding: "12px 18px", borderBottom: "1px solid #f4f3f9",
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <div style={{
                                            width: 26, height: 26, borderRadius: 8,
                                            background: hexToRgba(PURPLE, .12), color: PURPLE,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                <path d="M14 2v6h6"/><path d="M9 14l2 2 4-4"/>
                                            </svg>
                                        </div>
                                        <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1f1d2e" }}>전자결재 현황</span>
                                        <span style={{
                                            fontSize: 10.5, fontWeight: 600,
                                            background: hexToRgba(MINT, .15), color: MINT,
                                            padding: "2px 7px", borderRadius: 999, lineHeight: 1.45,
                                        }}>이번 달</span>
                                    </div>
                                    <Link to="/approval/draft-box" style={{
                                        color: "#8b8a99", fontSize: 12, fontWeight: 500,
                                        textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 2,
                                    }}>
                                        전체 보기
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "rotate(-90deg)" }}><path d="M6 9l6 6 6-6"/></svg>
                                    </Link>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))" }}>
                                    {EAPP_STAT_DEFS.map((def, i) => {
                                        const c = def.color || PURPLE;
                                        const bg = def.iconBg || hexToRgba(PURPLE, .12);
                                        return (
                                            <button key={def.key}
                                                onClick={() => navigate(def.route)}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 11,
                                                    padding: "14px 18px", background: "transparent", cursor: "pointer",
                                                    fontFamily: "inherit", textAlign: "left",
                                                    border: "none",
                                                    borderRight: i < EAPP_STAT_DEFS.length - 1 ? "1px solid #f4f3f9" : "none",
                                                    transition: "background 120ms",
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = hexToRgba(c, .04); }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: 9,
                                                    background: bg, color: c,
                                                    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                }}>{def.icon}</div>
                                                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                                                    <span style={{ fontSize: 11.5, color: "#8b8a99", fontWeight: 500 }}>{def.label}</span>
                                                    <span style={{ fontSize: 18, fontWeight: 700, color: "#1f1d2e", marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
                                                        {eappStats[def.key]}
                                                        <span style={{ fontSize: 11.5, color: "#8b8a99", fontWeight: 500, marginLeft: 3 }}>건</span>
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* 보고서 현황 카드 */}
                            <div style={{
                                background: "white", borderRadius: 14,
                                border: "1px solid #f0eff5",
                                boxShadow: "0 8px 24px -10px rgba(60,50,120,.18)",
                                overflow: "hidden",
                            }}>
                                <div style={{
                                    padding: "12px 18px", borderBottom: "1px solid #f4f3f9",
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <div style={{
                                            width: 26, height: 26, borderRadius: 8,
                                            background: hexToRgba(MINT, .12), color: MINT,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                                                <path d="M14 3v6h6"/><path d="M8 13h8M8 17h5"/>
                                            </svg>
                                        </div>
                                        <span style={{ fontSize: 13.5, fontWeight: 600, color: "#1f1d2e" }}>보고서 현황</span>
                                        <span style={{
                                            fontSize: 10.5, fontWeight: 600,
                                            background: hexToRgba(PURPLE, .15), color: PURPLE,
                                            padding: "2px 7px", borderRadius: 999, lineHeight: 1.45,
                                        }}>실시간</span>
                                    </div>
                                    <Link to="/report" style={{
                                        color: "#8b8a99", fontSize: 12, fontWeight: 500,
                                        textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 2,
                                    }}>
                                        전체 보기
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "rotate(-90deg)" }}><path d="M6 9l6 6 6-6"/></svg>
                                    </Link>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))" }}>
                                    {REPORT_STAT_DEFS.map((def, i) => {
                                        const c = def.color || PURPLE;
                                        const bg = def.iconBg || hexToRgba(PURPLE, .12);
                                        return (
                                            <button key={def.key}
                                                onClick={() => navigate(def.route, { state: { tab: 'received', statusFilter: def.statusFilter } })}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 11,
                                                    padding: "14px 18px", background: "transparent", cursor: "pointer",
                                                    fontFamily: "inherit", textAlign: "left",
                                                    border: "none",
                                                    borderRight: i < REPORT_STAT_DEFS.length - 1 ? "1px solid #f4f3f9" : "none",
                                                    transition: "background 120ms",
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = hexToRgba(c, .04); }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: 9,
                                                    background: bg, color: c,
                                                    display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                                }}>{def.icon}</div>
                                                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2, minWidth: 0, flex: 1 }}>
                                                    <span style={{ fontSize: 11.5, color: "#8b8a99", fontWeight: 500 }}>{def.label}</span>
                                                    <span style={{ fontSize: 18, fontWeight: 700, color: "#1f1d2e", marginTop: 3, fontVariantNumeric: "tabular-nums" }}>
                                                        {reportStats[def.key]}
                                                        <span style={{ fontSize: 11.5, color: "#8b8a99", fontWeight: 500, marginLeft: 3 }}>건</span>
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2열 그리드: 진행중 프로젝트 + (부가세 계산기 + 알림) */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.55fr) minmax(0, 1fr)",
                        gap: 20, marginBottom: 20,
                    }}>
                        {/* 진행 중인 프로젝트 */}
                        <div style={card}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <h2 onClick={() => setProjectTab('ongoing')} style={{ margin: 0, fontSize: 15, fontWeight: projectTab === 'ongoing' ? 700 : 500, color: projectTab === 'ongoing' ? "#1f1d2e" : "#bdbcc8", cursor: "pointer", transition: "color 120ms" }}>진행 중인 프로젝트</h2>
                                    <span style={{ color: "#d4d3df", fontSize: 14 }}>/</span>
                                    <h2 onClick={() => setProjectTab('upcoming')} style={{ margin: 0, fontSize: 15, fontWeight: projectTab === 'upcoming' ? 700 : 500, color: projectTab === 'upcoming' ? "#1f1d2e" : "#bdbcc8", cursor: "pointer", transition: "color 120ms" }}>예정된 프로젝트</h2>
                                    <span style={{
                                        fontSize: 11, fontWeight: 600,
                                        background: projectTab === 'ongoing' ? hexToRgba(MINT, 0.15) : hexToRgba(PURPLE, 0.12),
                                        color: projectTab === 'ongoing' ? MINT : PURPLE,
                                        padding: "2px 8px", borderRadius: 999, lineHeight: 1.45,
                                    }}>{projectTab === 'ongoing' ? ongoingProjects.length : upcomingProjects.length}</span>
                                </div>
                                <Link to="/project/list" style={{
                                    background: "transparent", border: "none", color: "#8b8a99",
                                    fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
                                    textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 2,
                                }}>
                                    전체 보기
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "rotate(-90deg)" }}><path d="M6 9l6 6 6-6"/></svg>
                                </Link>
                            </div>

                            {(() => {
                                const shown = projectTab === 'ongoing' ? ongoingProjects : upcomingProjects;
                                const isUpcoming = projectTab === 'upcoming';
                                if (shown.length === 0) {
                                    return (
                                        <p style={{ color: "#aaa9b7", fontSize: 13.5, textAlign: "center", padding: "32px 0" }}>
                                            {isUpcoming ? '예정된 프로젝트가 없습니다.' : '참여 중인 프로젝트가 없습니다.'}
                                        </p>
                                    );
                                }
                                return (
                                    <div style={{ maxHeight: 540, overflowY: "auto", paddingRight: 4 }}>
                                        {shown
                                            .slice().sort((a, b) => new Date(a.contractualStartDate) - new Date(b.contractualStartDate))
                                            .map((project, i) => {
                                                const dDay = project.totalDays - project.elapsedDays;
                                                return (
                                                    <div key={i}
                                                        onClick={() => navigate('/gantt-chart', { state: { projectId: project.projectId } })}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 16,
                                                            padding: "16px 4px",
                                                            borderBottom: "1px solid #f4f3f9",
                                                            cursor: "pointer",
                                                        }}>
                                                        <Donut value={isUpcoming ? 0 : project.contractualProgress} color={isUpcoming ? PURPLE : MINT} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                                                <span style={{
                                                                    fontSize: 10.5, fontWeight: 600, color: isUpcoming ? PURPLE : MINT,
                                                                    background: isUpcoming ? hexToRgba(PURPLE, 0.12) : hexToRgba(MINT, 0.14),
                                                                    padding: "2px 8px", borderRadius: 999, lineHeight: 1.45,
                                                                }}>{isUpcoming ? '예정' : '진행중'}</span>
                                                                {!isUpcoming && (
                                                                    <span style={{ fontSize: 11, color: "#aaa9b7", fontWeight: 500 }}>
                                                                        만기 D-Day:{" "}
                                                                        <span style={{ color: dDay < 60 ? "#d97757" : "#5d5b6e", fontWeight: 600 }}>
                                                                            {dDay}일 남음
                                                                        </span>
                                                                    </span>
                                                                )}
                                                                {!isUpcoming && project.actualProgress !== project.contractualProgress && (
                                                                    <span style={{ fontSize: 10.5, color: "#8b8a99" }}>
                                                                        실적 {project.actualProgress}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{
                                                                fontSize: 14, fontWeight: 600, color: "#1f1d2e",
                                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                                marginBottom: 4,
                                                            }}>
                                                                {project.projectName}
                                                            </div>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11.5, color: "#8b8a99", flexWrap: "wrap" }}>
                                                                <span>
                                                                    {dayjs(project.contractualStartDate).format('YYYY.MM.DD')} ~{" "}
                                                                    {dayjs(project.contractualEndDate).format('YYYY.MM.DD')} · {project.totalMonths}개월
                                                                    {project.extended && (
                                                                        <span style={{ marginLeft: 4, fontWeight: 700, color: "#7c3aed" }}>(연장)</span>
                                                                    )}
                                                                </span>
                                                                <span style={{ color: "#d4d3df" }}>|</span>
                                                                <span>
                                                                    고객사 ·{" "}
                                                                    <span style={{ color: "#3a3849", fontWeight: 500 }}>{project.client || '-'}</span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* 우측 컬럼: 부가세 계산기 + 알림 */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
                            {/* 부가세 계산기 */}
                            <div style={card}>
                                <VatCalculator />
                            </div>

                            {/* 최신 활동 및 알림 */}
                            <div style={{ ...card, padding: "20px 0 8px", display: "flex", flexDirection: "column", minHeight: 0 }}>
                                <div style={{ padding: "0 20px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{
                                            width: 28, height: 28, borderRadius: 8,
                                            background: hexToRgba(MINT, 0.15),
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            color: MINT,
                                        }}>
                                            <FaBell size={14} />
                                        </div>
                                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1f1d2e" }}>최신 활동 및 알림</h3>
                                        <span style={{
                                            fontSize: 11, fontWeight: 600,
                                            background: hexToRgba(PURPLE, 0.12), color: PURPLE,
                                            padding: "2px 7px", borderRadius: 999, lineHeight: 1.4,
                                        }}>{unreadActivities.length}</span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5 }}>
                                        <button onClick={loadActivities} style={{
                                            background: "transparent", border: "none", cursor: "pointer",
                                            padding: "4px 6px", borderRadius: 6,
                                            color: "#8b8a99", display: "flex", alignItems: "center", gap: 4,
                                            fontWeight: 600, fontFamily: "inherit", transition: "color 0.15s, background 0.15s"
                                        }} onMouseEnter={(e) => { e.currentTarget.style.color = "#1f1d2e"; e.currentTarget.style.background = "#f4f3f9"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = "#8b8a99"; e.currentTarget.style.background = "transparent"; }}>
                                            <FaSync size={11} />
                                            새로고침
                                        </button>
                                        <div style={{ width: 1, height: 12, background: "#e0dfe6", margin: "0 4px" }} />
                                        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            {['desc', 'asc'].map((order, i) => (
                                                <React.Fragment key={order}>
                                                    {i > 0 && <span style={{ color: "#d4d3df" }}>·</span>}
                                                    <button onClick={() => setSortOrder(order)} style={{
                                                        background: "transparent", border: "none", cursor: "pointer",
                                                        padding: "4px 8px", borderRadius: 6,
                                                        color: sortOrder === order ? "#1f1d2e" : "#aaa9b7",
                                                        fontWeight: sortOrder === order ? 600 : 500,
                                                        fontFamily: "inherit",
                                                    }}>
                                                        {order === 'desc' ? '최신순' : '오래된순'}
                                                    </button>
                                                </React.Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ flex: 1, overflowY: "auto", maxHeight: 260, display: "flex", flexDirection: "column", padding: "0 8px 8px" }}>
                                    {unreadActivities.length > 0 ? unreadActivities.map((activity) => {
                                        const cm = NOTIF_CAT_META[TYPE_TO_CODE[activity.type]] || NOTIF_CAT_META.ALARM;
                                        return (
                                            <div
                                                key={activity.id}
                                                onClick={() => handleActivityClick(activity)}
                                                style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", borderRadius: 10, cursor: "pointer" }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = "#fafaff"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                                            >
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                                    background: cm.bg, color: cm.c,
                                                    display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1,
                                                }}>
                                                    {cm.icon(13)}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: "#aaa9b7", fontWeight: 600, marginBottom: 2 }}>
                                                        <span>{activity.time}</span>
                                                        <span style={{ color: "#d4d3df" }}>·</span>
                                                        <span style={{ color: cm.c, fontWeight: 700 }}>{cm.label}</span>
                                                    </div>
                                                    {activity.title && (
                                                        <div style={{ fontSize: 12.5, color: "#3a3849", fontWeight: 600, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                            {activity.title}
                                                        </div>
                                                    )}
                                                    <div style={{
                                                        fontSize: activity.title ? 11.5 : 12.5, color: activity.title ? "#7a7989" : "#3a3849", lineHeight: 1.45,
                                                        overflow: "hidden", textOverflow: "ellipsis",
                                                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                                        wordBreak: "break-word"
                                                    }}>
                                                        {activity.textMessage}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleMarkRead(activity.id); }}
                                                    style={{
                                                        background: "transparent", border: "none", padding: 4, cursor: "pointer",
                                                        color: "#c7c5d2", display: "flex", marginTop: 2, borderRadius: 6,
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.color = "#5d5b6e"; e.currentTarget.style.background = "#ececf2"; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.color = "#c7c5d2"; e.currentTarget.style.background = "transparent"; }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M6 6l12 12M18 6L6 18" />
                                                    </svg>
                                                </button>
                                            </div>
                                        );
                                    }) : (
                                        <div style={{ textAlign: "center", color: "#aaa9b7", fontSize: 12.5, padding: "32px 12px" }}>
                                            새 알림이 없습니다.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}

function adminLinkStyle(color) {
    return {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "12px 16px", borderRadius: 10,
        background: hexToRgba(color, 0.06),
        border: `1px solid ${hexToRgba(color, 0.15)}`,
        color: "#1f1d2e", fontSize: 14, fontWeight: 500,
        textDecoration: "none", transition: "background 120ms",
    };
}
